import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  faBox,
  faChevronUp,
  faPencilAlt,
  faPlus,
  faUserPlus,
  faMouse,
} from '@fortawesome/free-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Choice } from '../data/class';
import { ProgramService } from '../services/program.service';
import { PromotionTypeService } from '../services/promotion-type.service';
import { StatusHelperService } from '../services/helpers/status-helper.service';
import { Package, Program, Promotion } from '../data/model';
import { PromotionModalComponent } from '../promotion-modal/promotion-modal.component';
import { ToastrService } from 'ngx-toastr';
import { PackageModalComponent } from '../package-modal/package-modal.component';
import { PromotionService } from '../services/promotion.service';
import { GlobalEventService } from '../services/global-event.service';
import { parseResponse } from '../data/parseResponseFunction';
import { Location } from '@angular/common';

@Component({
  selector: 'app-promotions',
  templateUrl: './promotions.component.html',
  styleUrls: ['./promotions.component.scss'],
})
export class PromotionsComponent implements OnInit {
  promotion: Promotion = new Promotion();
  program: Program;
  limit: number;
  faPlus = faPlus;
  faBox = faBox;
  faMouse = faMouse;
  faUserPlus = faUserPlus;
  programStructure: any = {};
  selectedPackage: any;
  selectedPromotion: any;
  programPackages: any[] = [];
  lockedTables = false;
  filteredStatuses: string[] = [];
  statuses: Choice[];
  campaignsFilter: any[];
  defaultStatuses = ['ACTIVE', 'PENDING', 'STARTED'];
  programId: number;
  promotionId: number;
  defaultStatusFilter = { statuses: this.defaultStatuses };
  sponsorKeyword = '';
  promotionTypes = {};
  colorCnt = -1;
  colors = [
    'promotion-type-green',
    'promotion-type-red',
    'promotion-type-blue',
    'promotion-type-purple',
    'promotion-type-lightgreen',
    'promotion-type-cyan',
    'promotion-type-beige',
    'promotion-type-yellow',
    'promotion-type-grey',
    'promotion-type-lavender',
    'promotion-type-lemon',
    'promotion-type-pink',
  ];
  statusHelper: any[];
  faChevronUp = faChevronUp;
  faPencilAlt = faPencilAlt;
  activeId = 1;
  selectedPackageId: number | null = null;
  // Sort promotions alphabetically by name (new default) or by original creation order ("id", pre-change behavior).
  sortBy: 'name' | 'id' = 'name';
  private static readonly SORT_BY_STORAGE_KEY = 'promotionManagement.sortBy';

  constructor(
    private router: ActivatedRoute,
    private modalHelper: NgbModal,
    private title: Title,
    private promotionTypeService: PromotionTypeService,
    private statusHelperService: StatusHelperService,
    private toastrService: ToastrService,
    private globalEventService: GlobalEventService,
    private promotionService: PromotionService,
    private programService: ProgramService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.promotion.status = '';
    this.promotion.typeName = '';
    this.title.setTitle('Promotions');
    this.statusHelper = this.statusHelperService.getStatus('badge');
    this.activeId = this.promotionService.activeId;
    this.sortBy = this.loadStoredSortBy();

    this.router.params.subscribe((params: any) => {
      this.promotion.id = +params.id;
      this.programId = this.promotion.id;
      this.promotionId = +params.promotionId;
      if (this.promotionService.selectedPackageId) {
        this.selectedPackageId = this.promotionService.selectedPackageId;
        this.selectedPackage = { id: this.promotionService.selectedPackageId };
        this.promotionService.selectedPackageId = this.selectedPackageId;
        this.getProgramStructure(this.selectedPackageId);
      } else {
        this.getProgramStructure();
      }
    });
  }

  private getProgramStructure(packageId?: number) {
    const params: any = {
      sortBy: this.sortBy,
      sortDir: 'ASC',
    };
    if (packageId) {
      params.packageId = packageId;
    }
    this.programService
      .getProgramStructure(this.programId, params)
      .subscribe((data: any) => {
        if (data.success) {
          this.programStructure = parseResponse(data);
          if (this.programStructure.promotions) {
            this.sinkInactivePromotions(this.programStructure.promotions);
          }
          this.getPromotionTypesForProgram();
          if (this.promotionId) {
            this.programStructure.promotions.forEach((promo: any) => {
              if (promo.id === this.promotionId) {
                this.selectPromotion(promo, null);
              }
              if (promo.children && promo.children.length > 0) {
                this.checkChildren(promo);
              }
            });
          }
          this.getProgramPackages();
          this.getProgram(this.programId);
        }
      });
  }

  getAllPromotionTypes(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.promotionTypeService
        .getAllPromotionTypes(this.defaultStatusFilter)
        .subscribe((data: any) => {
          if (data.success) {
            resolve(parseResponse(data));
          } else {
            reject([]);
          }
        });
    });
  }

  getProgramPromotions(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.programService
        .getProgramPromotions(this.programStructure.id)
        .subscribe((data: any) => {
          if (data.success) {
            resolve(parseResponse(data));
          } else {
            reject([]);
          }
        });
    });
  }

  promotionModal(id?: number): void {
    Promise.all([
      this.getProgramPromotions(),
      this.getAllPromotionTypes(),
    ]).then((result: any) => {
      const [promotions, types] = result;
      if (id) {
        this.promotionService.getPromotion(id).subscribe((promotion: any) => {
          if (promotion.success) {
            this.openPromotionModal(
              promotions,
              types,
              this.programId,
              promotion.entity,
              true
            );
          }
        });
      } else {
        this.openPromotionModal(promotions, types, this.programId);
      }
    });
  }

  openPromotionModal(
    promotions: Promotion[],
    types: any[],
    programId: number,
    promotion?: Promotion,
    isEdit?: boolean
  ): void {
    const instance = this.modalHelper.open(PromotionModalComponent);
    instance.componentInstance.promotions = promotions;
    instance.componentInstance.types = types;
    instance.componentInstance.promotion = promotion;
    instance.componentInstance.isEdit = isEdit;
    instance.componentInstance.programId = programId;
    instance.componentInstance.successEvent.subscribe((data: any) => {
      if (data) {
        this.getProgramStructure();
        this.toastrService.success(
          `Promotion successfully ${isEdit ? 'updated' : 'created'}`
        );
        instance.close();
      }
    });
  }

  packageModal(id?: number): void {
    if (id) {
      this.programService.getProgramPackage(id).subscribe((data: any) => {
        if (data.success) {
          this.openPackagesModal(true, this.programId, data.entity);
        }
      });
    } else {
      this.openPackagesModal(false, this.programId);
    }
  }

  openPackagesModal(isEdit: boolean, programId: number, pac?: Package): void {
    const instance = this.modalHelper.open(PackageModalComponent);
    instance.componentInstance.isEdit = isEdit;
    instance.componentInstance.programId = programId;
    instance.componentInstance.package = pac;
    instance.componentInstance.successEvent.subscribe((data: any) => {
      if (data) {
        this.getProgramPackages();
        this.toastrService.success(
          `Package successfully ${isEdit ? 'updated' : 'created'}`
        );
        instance.close();
      }
    });
  }

  selectPromotion(promo: Promotion, event?: any): void {
    this.selectedPromotion = promo;
    this.sendDataBreadCrumbs();
    if (event) {
      event.stopPropagation();
    }
  }

  private sendDataBreadCrumbs() {
    const dataToSend: any = {};
    dataToSend.data = {
      promotionId: this.selectedPromotion.id,
      programId: this.programId,
    };
    dataToSend.toComponent = 'bread-crumbs';
    this.globalEventService.sendGlobalEvent(dataToSend);
  }

  setPromotions(packageToSet): void {
    this.selectedPackage = packageToSet;
    this.selectedPackageId = packageToSet.id;
    this.promotionService.selectedPackageId = this.selectedPackageId;
    console.log(this.selectedPackageId, 'his.selectedPackageId');
    
    const dataToSend: any = {};
    dataToSend.data = { promotionId: '', programId: this.programId };
    dataToSend.toComponent = 'bread-crumbs';
    this.globalEventService.sendGlobalEvent(dataToSend);
    if (this.selectedPackageId) {
      this.getProgramStructure(this.selectedPackageId);
    } else {
      this.getProgramStructure();
    }
  }

  onSortByChanged(sortBy: 'name' | 'id'): void {
    this.sortBy = sortBy;
    this.saveStoredSortBy(sortBy);
    if (this.selectedPackageId) {
      this.getProgramStructure(this.selectedPackageId);
    } else {
      this.getProgramStructure();
    }
  }

  private loadStoredSortBy(): 'name' | 'id' {
    try {
      const stored = window.localStorage.getItem(
        PromotionsComponent.SORT_BY_STORAGE_KEY
      );
      return stored === 'id' ? 'id' : 'name';
    } catch {
      // localStorage may be unavailable (e.g. private browsing); fall back to the default.
      return 'name';
    }
  }

  private saveStoredSortBy(sortBy: 'name' | 'id'): void {
    try {
      window.localStorage.setItem(
        PromotionsComponent.SORT_BY_STORAGE_KEY,
        sortBy
      );
    } catch {
      // Ignore write failures (e.g. storage disabled/full); sorting still works for this session.
    }
  }

  // Client-side only: sinks non-ACTIVE promotions to the bottom of each sibling list
  // (recursively through children), without altering the backend query/sort.
  private sinkInactivePromotions(promotions: any[]): void {
    if (!promotions || promotions.length === 0) {
      return;
    }
    promotions.sort((a: any, b: any) => {
      const aActive = a.status === 'ACTIVE' ? 0 : 1;
      const bActive = b.status === 'ACTIVE' ? 0 : 1;
      return aActive - bActive;
    });
    promotions.forEach((promo: any) => {
      if (promo.children && promo.children.length > 0) {
        this.sinkInactivePromotions(promo.children);
      }
    });
  }

  getPromotionTypesForProgram(): void {
    if (this.programStructure && this.promotionTypes) {
      this.programStructure.promotions.forEach((promo: any) => {
        // first time we see a type it goes to a map:
        if (this.promotionTypes[promo.typeName]) {
          promo.typeColor = this.promotionTypes[promo.typeName];
        } else {
          this.promotionTypes[promo.typeName] = this.getNextColor();
          promo.typeColor = this.promotionTypes[promo.typeName];
        }
        if (promo.children && promo.children.length > 0) {
          this.assignColorToChildren(promo);
        }
      });
    }
  }

  getNextColor(): string {
    this.colorCnt++;
    if (this.colorCnt === this.colors.length) {
      this.colorCnt = 0;
    }
    return this.colors[this.colorCnt];
  }

  assignColorToChildren(promo: Promotion): void {
    promo.children.forEach((child: any) => {
      // first time we see a type it goes to a map:
      if (this.promotionTypes[child.typeName]) {
        child.typeColor = this.promotionTypes[child.typeName];
      } else {
        this.promotionTypes[child.typeName] = this.getNextColor();
        child.typeColor = this.promotionTypes[child.typeName];
      }
      if (child.children && child.children.length > 0) {
        this.assignColorToChildren(child);
      }
    });
  }

  checkChildren(promo: Promotion): void {
    promo.children.forEach((child: any) => {
      if (child.id === this.promotionId) {
        this.selectPromotion(child);
      }
      if (child.children && child.children.length > 0) {
        this.checkChildren(child);
      }
    });
  }

  getProgramPackages(): void {
    this.programService
      .getProgramPackages(this.programStructure.id, {})
      .subscribe((data: any) => {
        if (data.success) {
          this.programPackages = parseResponse(data);
        }
      });
  }

  getProgram(id: number): void {
    this.programService.getProgram(id).subscribe((data: any) => {
      if (data.success) {
        this.program = data.entity;
      }
    });
  }

  promotionChanged(event: Promotion): void {
    const currentUrl = this.location.path();
    const newUrl = `${currentUrl.replace(/\/\d+$/, '')}/${event.id}`;
    this.location.replaceState(newUrl);
    this.selectedPromotion = event;
    this.sendDataBreadCrumbs();
  }
  changeTab(index: number): void {
    this.promotionService.activeId = index;
  }
}
